import { useState } from "react";
import { createPortal } from "react-dom";
import { IonContent, IonList, IonItem, IonLabel, IonBadge, IonRadio, IonRadioGroup, IonAvatar, IonBackdrop, IonSpinner } from "@ionic/react";
import FreshPopover from "./FreshPopover";
import { uiAvatar } from "./SidebarProfileCard";
import { useSavedAssets, refreshSavedAssets } from "./SavedAssets";
import { userHeaders } from "@/utils/userAuth";
import { emitAppLive } from "@/utils/appLive";
import { toast } from "@/utils/toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const SWITCH_MIN_MS = 600; // the loading screen stays long enough to read as one, never a flash

// The business switcher popover: every business on the account with its logo,
// address line and plan status, and a radio on the one being operated as.
// Picking another switches the account server-side behind a full-screen
// backdrop and spinner, loads the account's shared data, then lets every open
// page refresh its own (the sidebar card, Billing, employees, signatures, the
// gated features all follow). Shared by the sidebar's business card and the
// Billing page's.
export default function BusinessSwitcherPopover({ open, event, onClose, reference = "trigger" }) {
  const { businesses, activeBusinessId } = useSavedAssets();
  const [switching, setSwitching] = useState(false); // the whole screen waits while the business changes

  const switchBusiness = async (id) => {
    if (!id || id === activeBusinessId || switching) return;
    const name = (businesses.find((b) => b.id === id) || {}).name || "";
    onClose();
    setSwitching(true);
    const started = Date.now();
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/businesses/${id}/activate`, { method: "PUT", headers: userHeaders() });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not switch");
      // the shared account data first, then every open page re-fetches its own
      await refreshSavedAssets();
      emitAppLive({ reason: "business-switch" });
      await new Promise((r) => setTimeout(r, Math.max(0, SWITCH_MIN_MS - (Date.now() - started))));
      toast.success(`Now managing ${name}`.trim());
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSwitching(false);
    }
  };

  return (
    <>
    {switching && createPortal(
      /* the stock Ionic backdrop over everything, the default spinner in the
         brand colour centred on it; mounted only while switching */
      <div className="biz-switching" style={{ position: "fixed", inset: 0, zIndex: 20000, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <IonBackdrop visible tappable={false} stopPropagation style={{ opacity: "var(--ion-backdrop-opacity, 0.32)" }} />
        <IonSpinner color="primary" style={{ position: "relative", zIndex: 1 }} />
      </div>,
      document.querySelector("ion-app") || document.body,
    )}
    <FreshPopover
      open={open}
      event={event}
      onClose={onClose}
      side="bottom"
      alignment="start"
      reference={reference}
      style={{ "--width": "340px", "--offset-y": "4px" }}
    >
      <IonContent>
        <IonRadioGroup value={activeBusinessId} onIonChange={(e) => switchBusiness(e.detail.value)}>
          <IonList lines="full" className="biz-switcher" style={{ padding: "4px 0" }}>
            {businesses.map((b, i) => (
              <IonItem key={b.id} button detail={false} lines={i === businesses.length - 1 ? "none" : "full"}
                onClick={() => switchBusiness(b.id)} style={{ "--min-height": "64px", "--padding-start": "14px", "--inner-padding-end": "14px" }}>
                <IonAvatar slot="start" style={{ width: 40, height: 40, marginRight: 14, background: "var(--ion-color-step-100)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                  {/* no logo: the initial the sidebar's business card shows */}
                  <img alt="" src={b.logo || uiAvatar(b.name || "Business")} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </IonAvatar>
                <IonLabel>
                  <h3 style={{ fontWeight: 700, fontSize: "0.95rem", margin: 0 }}>{b.name}</h3>
                  {b.addressLine && <p style={{ margin: "2px 0 3px", fontSize: "0.78rem", color: "var(--ion-color-medium)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{b.addressLine}</p>}
                  <IonBadge color={{ trialing: "warning", active: "success", cancelling: "warning" }[b.status] || "medium"} style={{ fontSize: "0.68rem" }}>
                    {{ trialing: "Trialing", active: "Active", cancelling: "Cancelling", canceled: "Canceled" }[b.status] || "Needs a plan"}
                  </IonBadge>
                </IonLabel>
                <IonRadio slot="end" value={b.id} aria-label={`Operate as ${b.name}`} />
              </IonItem>
            ))}
          </IonList>
        </IonRadioGroup>
      </IonContent>
    </FreshPopover>
    </>
  );
}
